import { Add, Info, UploadFile } from '@mui/icons-material';
import {
    Box,
    Button,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import { useRef } from 'react';
import { useToast } from 'src/contexts/ToastContext';
import { EmptyState } from '../common/EmptyState';

interface EmptyRankingStateProps {
    onImportCsv: (csvContent: string) => Promise<void>;
    onShowAddRow: () => void;
    hasCultureSelected: boolean;
}

export const EmptyRankingState = ({
    onImportCsv,
    onShowAddRow,
    hasCultureSelected,
}: EmptyRankingStateProps): JSX.Element => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { showToast } = useToast();

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const file = event.target.files?.[0];

        if (!file) return;

        try {
            const content = await file.text();

            await onImportCsv(content);
            showToast('CSV imported successfully', 'success');
        } catch (error) {
            console.error('Error importing CSV:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to import CSV';

            showToast(errorMessage, 'error');
        }

        // Reset the input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <EmptyState sx={{ p: 4 }}>
            <Box sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
            }}
            >
                <Typography gutterBottom variant="h6">
                    {hasCultureSelected
                        ? 'No design concepts added yet'
                        : 'Select a culture to get started'}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
                    {hasCultureSelected ? (
                        'Design concept previews depend on the selected culture. Please select a culture above before adding rankings.'
                    ) : (
                        'Add design concepts manaully or import a CSV to get started.'
                    )}
                </Typography>
                <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <input
                            hidden
                            accept=".csv"
                            ref={fileInputRef}
                            type="file"
                            onChange={handleFileUpload}
                        />
                        <Button
                            color="primary"
                            disabled={!hasCultureSelected}
                            startIcon={<UploadFile />}
                            variant="outlined"
                            onClick={(): void => fileInputRef.current?.click()}
                        >
                            Import CSV
                        </Button>
                        <Tooltip title={hasCultureSelected
                            ? 'Upload a CSV with a \'Design Concept ID\' column'
                            : 'Select a culture first to enable CSV import'}
                        >
                            <IconButton size="small">
                                <Info color="primary" fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Button
                        aria-label="Add Concept"
                        color="primary"
                        disabled={!hasCultureSelected}
                        startIcon={<Add />}
                        variant="outlined"
                        onClick={onShowAddRow}
                    >
                        Add Concept
                    </Button>
                </Box>
            </Box>
        </EmptyState>
    );
};
